import keyBy from 'lodash-es/keyBy';
import { ActionContext } from 'vuex';

import { STATUSES, UI_TIMEOUT } from '@/constants';
import { TimestampedResponse } from '@/models';
import { Contact } from '@/models/contact';
import { Credit, CreditDownload } from '@/models/credit';
import { Customer, CustomerGroup, PaymentActivation, ServiceZone, Zone } from '@/models/customer';
import { CustomerContact } from '@/models/customerContact';
import { Product } from '@/models/product';
import { Tax } from '@/models/tax';
import notify from '@/notify';
import i18n from '@/plugins/i18n';
import api from '@/services/api';
import { BulkSaveZonesRequestPayload, GetZonesRequestPayload, UpdateZoneRequestPayload } from '@/services/api/CRM';
import parsers from '@/services/parsers';
import { handleError } from '@/utils/errors';
import JSONTransform from '@/utils/json-transform';

import { CRMState } from './state';

/**
 * Used in Customers List page
 * @param commit
 * @param payload
 */
export async function fetchCustomersWithOrders(
  { commit },
  payload: { search?: string; archived?: boolean; timestamp?: string } = {},
): Promise<TimestampedResponse> {
  // customers list page needs to fetch both archived and non-archived customers
  payload.archived = true;

  commit('UPDATE_IS_LOADING_CUSTOMERS', true);
  let response;
  let customers;
  let customerStats;

  try {
    try {
      customers = await api.CRM.getCustomers(payload);
      customerStats = await api.CRM.getStats();
    } catch (error) {
      handleError(error, i18n.t('crm.notify.fetchCustomersError'));
    }

    // join customers with stats
    const customersWithStats = customers?.map((c) => {
      const stats = customerStats[c.id];

      return {
        ...c,
        ...stats,
      };
    });

    response = {
      results: customersWithStats,
      timestamp: payload.timestamp,
    };
  } finally {
    commit('UPDATE_IS_LOADING_CUSTOMERS', false);
  }

  return response;
}

/**
 * Fetches all Customers of the current Company.
 * @param {ActionContext} commit passed by vuex
 * @param {object} [payload] optional to pass params
 * @param {number} [payload.customerId] optional id to fetch a detailed Customer record
 */
export const fetchCustomers = async ({ commit }, payload: { customerId?: string } = {}) => {
  const { customerId } = payload;
  let fetched;

  commit('UPDATE_IS_LOADING_CUSTOMERS', true);

  try {
    if (customerId) {
      fetched = await api.CRM.getCustomers(payload).catch((error) => {
        handleError(error, i18n.t('crm.notify.fetchCustomersError'));
      });

      fetched.taxes?.forEach((tax) => {
        tax = <Tax>{
          autoApply: tax.autoApply,
          isEnabled: true,
          isEditable: tax.isEditable || false,
          name: tax.name,
          rate: parseFloat(tax.taxRatePct) * 100,
          taxId: tax.tax,
          taxAncillary: tax.taxAncillary,
          taxWholesale: tax.taxWholesale,
        };
      });

      commit('SAVE_CUSTOMER', fetched);
    } else {
      fetched = await api.CRM.getCustomers(payload);
      commit('SET_CUSTOMERS', fetched);
    }
  } finally {
    commit('UPDATE_IS_LOADING_CUSTOMERS', false);
  }

  return fetched;
};

/**
 * Fetches potential customers.
 */
// eslint-disable-next-line require-await
export const fetchPotentialCustomers = async (_obj, params) => {
  return api.CRM.getPotentialCustomers(params).catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchCustomersError'));
  });
};

/**
 * Saves a Customer.
 * @param {ActionContext} context passed by vuex
 * @param {Object} customer Customer information
 */
export const saveCustomer = async ({ commit }, customer: Customer) => {
  let saved;

  if (customer.id) {
    // update existing
    saved = await api.CRM.updateCustomer(customer).catch((error) => {
      handleError(error, i18n.t('crm.notify.customersSave'));
    });
  } else {
    saved = await api.CRM.addCustomer(customer).catch((error) => {
      handleError(error, i18n.t('crm.notify.customersSave'));
    });
  }

  commit('SAVE_CUSTOMER', saved);
  notify.success(i18n.t('notify.customerUpdated'));

  return saved;
};

/**
 * Saves a CustomerContact. This function serves only as an update since there is no create endpoint for this object
 * @param {ActionContext} context passed by vuex
 * @param {number} id The `id` of the CustomerContact to set primary
 * @param {Object} customerContact The new CustomerContact data to save
 */
export const saveCustomerContact = async (_, customerContact: CustomerContact) => {
  if (!customerContact.id) {
    return;
  }

  const saved = await api.CRM.updateCustomerContact(customerContact).catch((error) => {
    handleError(error, i18n.t('crm.notify.customerContactEdit'));
  });

  notify.success(i18n.t('crm.customerContactAddEdit.editSuccess'));

  return saved;
};

/**
 * Fetches all Contacts of the current Company.
 * @param {ActionContext} context passed by vuex
 */
export const fetchContacts = async ({ commit }) => {
  commit('SET_CONTACTS_LOADING', true);

  try {
    const contacts = await api.CRM.getContacts().catch((error) => {
      handleError(error, i18n.t('crm.notify.fetchCustomersError'));
    });

    commit('SET_CONTACTS', contacts);
    return contacts;
  } finally {
    commit('SET_CONTACTS_LOADING', false);
  }
};

/**
 * Fetches all CustomerContacts of the current Company.
 * @param {ActionContext} context passed by vuex
 */
export const loadCustomerContacts = async ({ commit }) => {
  commit('SET_CUSTOMER_CONTACTS_LOADING', true);

  try {
    const customerContacts = await api.CRM.getCustomerContacts().catch((error) => {
      handleError(error, i18n.t('crm.notify.customerContacts'));
    });

    commit('SET_CUSTOMER_CONTACTS', customerContacts);

    return customerContacts;
  } finally {
    commit('SET_CUSTOMER_CONTACTS_LOADING', false);
  }
};

/**
 * Saves a Contact.
 * @param {ActionContext} context passed by vuex
 * @param {Object} contact Contact information
 */
export const saveContact = async ({ commit }, contact: Contact) => {
  let saved;

  // TODO combine these into a single API? ie `saveContact` ?
  if (contact.id) {
    saved = await api.CRM.updateContact(contact).catch((error) => {
      handleError(error, i18n.t('crm.notify.contactSave'));
    });
  } else {
    saved = await api.CRM.addContact(contact).catch((error) => {
      handleError(error, i18n.t('crm.notify.contactSave'));
    });
  }

  commit('SAVE_CONTACT', saved);
  notify.success(i18n.t('crm.customer_add_edit.contact_updated'));

  return saved;
};

/**
 * Deletes a Contact.
 * @param {ActionContext} context passed by vuex
 * @param {Object} contact Contact information
 */
export const deleteContact = async ({ commit }, contact: Contact) => {
  await api.CRM.deleteContact(contact).catch((error) => {
    handleError(error, i18n.t('crm.notify.contactDelete'));
  });

  commit('DELETE_CONTACT', contact);
  notify.success('Contact Deleted');
};

/**
 * Fetch Distribution Zones by Company. By default, will use the Company as found
 * in the current URL. This cannot be overriden at this time.
 * @param payload
 * @param payload.fields - which fields to return for a zone
 * @param payload.zipcodes - which zipcodes to search for zones by
 */
export const fetchDistributionZones = async ({ commit }, payload: GetZonesRequestPayload) => {
  commit('SET_ZONES_LOADING', true);

  try {
    const zones = await api.CRM.getDistributionZones(payload).catch((error) => {
      handleError(error, i18n.t('crm.notify.fetchZones'));
    });

    commit('SET_ZONES', zones);

    return zones;
  } finally {
    commit('SET_ZONES_LOADING', false);
  }
};

/**
 * Fetch Distribution Zone by zone id.
 * @param {ActionContext} context - assed by vuex
 * @param id - distribution zone id
 */
export const fetchDistributionZone = async ({ commit, state, getters }, id: string) => {
  const zone = state.zones && getters.zone(id);

  if (zone) {
    return zone;
  }

  commit('SET_ZONES_LOADING', true);

  try {
    const zones = await api.CRM.getDistributionZone(id).catch((error) => {
      handleError(error, i18n.t('crm.notify.fetchZones'));
    });

    commit('SET_ZONES', zones);

    return zones;
  } finally {
    commit('SET_ZONES_LOADING', false);
  }
};

export interface SaveZonePayload extends Omit<Zone, 'id'> {
  id?: Zone['id'];
}

/**
 * Saves a new Distribution Zone.
 * @param context passed by vuex
 * @param zone the data used to create the new Zone
 */
export const addZone = async ({ commit }, zone: Omit<Zone, 'id'>) => {
  const response = await api.CRM.addZone(zone);

  commit('SAVE_ZONE', response);

  return response;
};

/**
 * Updates an existing Distribution Zone.
 * @param passed by vuex
 * @param zone the data used to update the Zone
 */
export const updateZone = async ({ commit }, zone: UpdateZoneRequestPayload) => {
  const response = await api.CRM.updateZone(zone);

  commit('SAVE_ZONE', response);

  return response;
};

/**
 * Saves multiple Distribution Zones. Can be new Zones or updating an existing ones.
 * @param {ActionContext} context passed by vuex
 * @param {Zone} zone the data used to update the Zone
 */
export const bulkSaveZones = async ({ commit }, zones: BulkSaveZonesRequestPayload[]) => {
  const response = await api.CRM.bulkSaveZones(zones).catch((error) => {
    handleError(error, i18n.t('crm.notify.importZonesError'));
  });

  response?.forEach((zone) => {
    commit('SAVE_ZONE', zone);
  });
  return response;
};

/**
 * Removes a Distribution Zone.
 * @param {ActionContext} context passed by vuex
 * @param {Zone} zone the Zone to remove.
 */
export const removeZone = async ({ commit }, zone: Zone) => {
  await api.CRM.removeZone(zone);
  commit('REMOVE_ZONE', zone);
  notify.success(`Zone "${zone.name}" removed`);

  return;
};

/**
 * Fetch Zipcodes available to a Distribution Zone.
 */
export const fetchZips = async ({ commit }) => {
  const zips = await api.CRM.getZips().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchZips'));
  });

  commit('SET_ZIPS', zips);

  return zips;
};

/**
 * Fetch License Types
 * @param {ActionContext} context passed by vuex
 */
export const fetchLicenseTypes = async ({ commit }) => {
  const licenseTypes = await api.CRM.getLicenseTypes().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchLicenseTypes'));
  });

  commit('SET_LICENSE_TYPES', licenseTypes);

  return licenseTypes;
};

/**
 * Fetch Payment Terms
 * @param {ActionContext} context passed by vuex
 */
export const fetchPaymentTerms = async ({ commit }) => {
  const paymentTerms = await api.CRM.getPaymentTerms().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchPaymentTerms'));
  });

  commit('SET_PAYMENT_TERMS', paymentTerms);

  return paymentTerms;
};

/**
 * Fetch Customer Statuses
 * @param {ActionContext} context passed by vuex
 */
export const fetchStatuses = async ({ commit }) => {
  const statuses = await api.CRM.getStatuses().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchCustomerStatus'));
  });

  commit('SET_STATUSES', statuses);

  return statuses;
};

/**
 * Fetch Customer Tiers
 * @param {ActionContext} context passed by vuex
 */
export const fetchTiers = async ({ commit }) => {
  const tiers = await api.CRM.getTiers().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchCustomerTier'));
  });

  commit('SET_TIERS', tiers);

  return tiers;
};

/**
 * Fetch Service Zones, will use the current signed user.
 * @param {ActionContext} context passed by vuex
 */
export const fetchServiceZones = async ({ commit }) => {
  const serviceZones = await api.CRM.getServiceZones().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchServicesZones'));
  });

  commit('SET_SERVICE_ZONES', serviceZones);

  return serviceZones;
};

/**
 * Saves a Service Zone, new one or existing.
 * @param {ActionContext} context passed by vuex
 * @param serviceZone
 */
export const saveServiceZone = async ({ commit }, serviceZone: ServiceZone) => {
  let saved;

  if (serviceZone.id) {
    // update existing
    saved = await api.CRM.updateServiceZone(serviceZone).catch((error) => {
      handleError(error, i18n.t('crm.notify.updateServiceZone'));
    });
  } else {
    const { id: _id, ...data } = serviceZone;

    saved = await api.CRM.addServiceZone(data).catch((error) => {
      handleError(error, i18n.t('crm.notify.createServiceZone'));
    });
  }

  commit('SAVE_SERVICE_ZONE', saved);
  return saved;
};

/**
 * Removes a Service Zone.
 * @param {ActionContext} context passed by vuex
 * @param serviceZone
 */
export const removeServiceZone = async ({ commit }, serviceZone: ServiceZone) => {
  await api.CRM.removeServiceZone(serviceZone);
  commit('REMOVE_SERVICE_ZONE', serviceZone);
  notify.success(`Service Zone "${serviceZone.name}" removed`);
};

/**
 * Update customers after a custom pricing is updated
 * @param {Customer[]} customers to be updated
 */
export const fetchTags = async ({ commit }) => {
  const tags = await api.CRM.getTags().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchTags'));
  });

  commit('SET_TAGS', tags);

  return tags;
};

/**
 * bulk insert customers from CSV file
 * @param commit
 * @param {Array} rows data containing CSV rows
 * @param {Boolean} validate data will be just validated and not saved if true
 * @param {function} onUploadProgress callback to retrieve the update progress value
 */
export const saveCSVRows = async (_commit, { rows, validate, onUploadProgress }) => {
  const data = rows.map((r) => ({ ...r.initialData, rowId: r.index }));

  const response = await api.CRM.saveCSVRows({ data, validate, onUploadProgress }).catch((error) => {
    handleError(error, i18n.t('crm.notify.saveCsvRows'));
  });

  const failedRows = new JSONTransform(response.failedRows).toSnakeCaseKeys().map((r) => {
    const errors: object[] = [];
    const initialData = {
      ROW_ID: undefined,
    };

    for (const k in r.errors) {
      r.errors[k].map((message) => errors.push({ field: k.toUpperCase(), message }));
    }

    for (const k in r.initial_data) {
      initialData[k.toUpperCase()] = r.initial_data[k];
    }

    return {
      index: initialData.ROW_ID,
      initialData,
      errors,
    };
  });

  const keyedFailedRows = keyBy(failedRows, 'index');

  const validRows = rows.filter((r) => !keyedFailedRows[r.index]);

  return {
    ...response,
    failedRows,
    validRows,
  };
};

/**
 * Get stats info for all customers (contains lastOrderDate for each customer)
 */
export const fetchStats = async ({ commit }) => {
  const customerStats = await api.CRM.getStats().catch((error) => {
    handleError(error, i18n.t('crm.notify.fetchStats'));
  });

  commit('SET_STATS', customerStats);

  return customerStats;
};

/**
 * Get tasks for all customers
 */
export const fetchTasks = async ({ commit }, payload) => {
  commit('SET_TASKS_LOADING', true);

  try {
    const tasks = await api.CRM.getTasks(payload).catch((error) => {
      handleError(error, i18n.t('crm.notify.fetchTasks'));
    });

    commit('SET_TASKS', tasks);

    return tasks;
  } finally {
    commit('SET_TASKS_LOADING', false);
  }
};

/**
 * Get a single task for a customers
 */
export const fetchTask = async ({ commit }, taskId) => {
  commit('SET_TASKS_LOADING', true);

  try {
    const task = await api.CRM.getTask(taskId).catch((error) => {
      handleError(error, i18n.t('crm.notify.fetchTask'));
    });

    commit('SET_TASK', task);

    return task;
  } finally {
    commit('SET_TASKS_LOADING', false);
  }
};

/**
 *
 * @param {ActionContext} context passed by vuex
 * @param {Object} task
 */
export const saveTask = async ({ commit }, task) => {
  let saved;

  if (task.id) {
    saved = await api.CRM.updateTask(task);
  } else {
    saved = await api.CRM.saveTask(task).catch((error) => {
      handleError(error, i18n.t('crm.notify.saveTask'));
    });
  }

  commit('SAVE_TASK', saved);
  notify.success(i18n.t('crm.tasks.taskSaved'));
  return saved;
};

/**
 *
 * @param {ActionContext} context passed by vuex
 * @param {Object} task
 */
export const saveRecurringTask = async ({ commit }, task) => {
  const saved = await api.CRM.saveTask(task).catch((error) => {
    handleError(error, i18n.t('crm.notify.saveTask'));
  });

  [].concat(saved).forEach((task) => {
    commit('SET_TASK', task);
  });
  notify.success(i18n.t('crm.tasks.recurringTaskSaved'));
  return saved;
};

/**
 * Fetches credits
 * @param {Object} params additional query parameter
 */
export const fetchCredits = async ({ commit }, params = {}) => {
  commit('LOADING_CREDITS', true);

  try {
    const response = await api.CRM.getCredits(params);

    commit('SET_CREDITS', response);
  } finally {
    commit('LOADING_CREDITS', false);
  }
};

function handleSaveCreditError(error) {
  handleError(error, i18n.t('crm.credits.error_save'));
}

/**
 * Saves a new or existing credit
 * @param {Credit} credit to be added or updated
 */
export const saveCredit = async ({ commit }, credit: Credit) => {
  let response;

  if (credit.id) {
    if (credit.internalStatus === STATUSES.CREDIT_INTERNAL.REMOVE) {
      response = await api.CRM.removeCreditFromOrder(credit.id).catch(handleSaveCreditError);
    } else {
      response = await api.CRM.updateCredit(credit).catch(handleSaveCreditError);
    }

    if (!credit.appliedTo) {
      notify.success(i18n.t('crm.notify.creditUpdated'));
    }
  } else {
    response = await api.CRM.addCredit(credit).catch(handleSaveCreditError);
    notify.success(i18n.t('crm.notify.newCreditAdded'));
  }

  commit('SAVE_CREDIT', response);

  return response;
};

/**
 * Archives credit
 * @param {Credit} credit to be added or updated
 */
export const archiveCurrentCredit = async ({ commit }, credit: Credit) => {
  await api.CRM.updateCredit(credit).catch((error) => {
    handleError(error, i18n.t('crm.credits.errorArchive'));
  });

  commit('ARCHIVE_CREDIT', credit);
  notify.success(i18n.t('crm.credits.creditArchived'));
};

/**
 * Exports credits
 */
export const exportCredits = async (_commit, creditDownload: CreditDownload) => {
  const { toEmail } = await api.CRM.exportCredits(creditDownload).catch((error) => {
    handleError(error, i18n.t('crm.credits.error_exporting_credits'));
  });

  notify.success(i18n.t('crm.credits.credit_export_success', { toEmail }));
};

/**
 * Fetch Inbound Leads
 */
export async function fetchInboundLeads() {
  const response = await api.CRM.getInboundLeads();

  return response.results;
}

/**
 * Retrieves a list of Customer that can buy a product
 * used in the Listings page
 * @param {Array[number]} productIds collection of productId's
 * @returns {Customer[]}
 */
export const fetchEligibleBuyersForProducts = async (
  { rootGetters }: ActionContext<CRMState, unknown>,
  productIds: Product['id'][],
) => {
  const response = await api.CRM.getEligibleBuyersForProducts(productIds);

  return parsers.CRM.buildEligibleBuyers(response, rootGetters['CRM/customersById']);
};

/**
 * Fetch Customer Groups
 * @returns {CustomerGroup[]}
 */
export const fetchCustomerGroups = async ({ commit }: ActionContext<CRMState, unknown>) => {
  commit('LOADING_GROUPS', true);

  try {
    const groups = await api.CRM.getCustomerGroups();

    commit('SET_GROUPS', groups);

    return groups;
  } finally {
    commit('LOADING_GROUPS', false);
  }
};

/**
 * Creates or updates a customer group
 */
export const saveCustomerGroup = async ({ commit }: ActionContext<CRMState, unknown>, group: CustomerGroup) => {
  let response;

  if (group.id) {
    response = await api.CRM.updateCustomerGroup(group);
  } else {
    response = await api.CRM.createCustomerGroup(group);
  }

  commit('SAVE_GROUP', response);
  notify.success(i18n.t(`crm.notify.${group.id ? 'groupUpdated' : 'groupSaved'}`));

  return response;
};

/**
 * Deletes a customer group
 * @param {CustomerGroup} group
 */
export const deleteCustomerGroup = async ({ commit }: ActionContext<CRMState, unknown>, group: CustomerGroup) => {
  await api.CRM.removeCustomerGroup(group.id).catch((error) => {
    handleError(error, i18n.t('crm.notify.errorDeleteGroup'));
  });

  commit('DELETE_GROUP', group);
  notify.success(i18n.t('crm.notify.groupDeleted'));
};

/**
 * Save CRM Linking
 */
export const saveCRMLinking = async ({ commit }, customer: Customer) => {
  let response;

  try {
    response = await api.CRM.updateCustomer(customer);

    notify.success(i18n.t('crm.notify.customerManagementSave', { buyer: response.buyerName }));
    setTimeout(() => {
      commit('SAVE_CRM_LINKING', response);
    }, UI_TIMEOUT);
  } catch (error) {
    handleError(error, i18n.t('crm.notify.customersSave'));
  }

  return response;
};

/**
 * Activate payment options
 */
export const activatePaymentOptions = async ({ commit }, paymentActivation: PaymentActivation) => {
  let response;

  try {
    response = await api.CRM.activatePaymentOptions(paymentActivation);

    notify.success(i18n.t('crm.notify.activatePaymentOptions'));
  } catch (error) {
    handleError(error, i18n.t('crm.notify.paymentActivationError'));
  }

  commit('SAVE_CUSTOMER_PAYMENT_ACTIVATION', response);

  return response;
};

/**
 * Fetches payment options of company customer
 */
export const fetchCustomerPaymentOptions = async ({ commit }, { customerId }) => {
  const response = await api.CRM.getCustomerPaymentOptions({ customerId });

  commit('SET_CUSTOMER_PAYMENT_OPTIONS', response);

  return response;
};
